import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Menu, X, User, LogOut, Settings, LayoutDashboard, Shield, MessageSquare, Home, Calendar, Users as UsersIcon, FileText } from 'lucide-react';
import NotificationBell from './NotificationBell';

export const Navbar = ({ mobileMenuOpen: externalMenuOpen, setMobileMenuOpen: externalSetMenuOpen }) => {
  const { user, logout, isAuthenticated, isCoach, isAdmin, isStreamManager } = useAuth();
  // Use external state if provided, otherwise use local state (for backwards compatibility)
  const [localMenuOpen, setLocalMenuOpen] = useState(false);
  const mobileMenuOpen = externalMenuOpen !== undefined ? externalMenuOpen : localMenuOpen;
  const setMobileMenuOpen = externalSetMenuOpen || setLocalMenuOpen;
  
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    setMobileMenuOpen(false);
    logout();
    navigate('/');
  };

  // Order matters: Library first (landing page after login), then Events, Community, Gallery, Marketplace
  const navLinks = [
    { href: '/library', label: 'Library' },
    { href: '/community', label: 'Community' },
    { href: '/marketplace', label: 'Marketplace' },
    { href: '/about', label: 'About Trent' },
    ...(isAdmin ? [{ href: '/store', label: 'Store' }] : []),
  ];

  const isLandingPage = location.pathname === '/';

  return (
    <nav className="sticky top-0 z-50 bg-background/90 backdrop-blur-md border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png" 
              alt="Elevated Roping Media" 
              className="h-10 w-auto"
            />
            <div className="flex flex-col">
              <span className="font-display font-bold text-sm tracking-tight text-foreground group-hover:text-primary transition-colors leading-none">
                ELEVATED
              </span>
              <span className="font-display font-bold text-xs tracking-wider text-muted-foreground leading-none">
                ROPING MEDIA
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {/* Logged-in: Library → Community → Events → Gallery → Marketplace */}
            {isAuthenticated && navLinks.filter(l => l.href !== '/about').map(link => (
              <Link
                key={link.href}
                to={link.href}
                className={`font-medium transition-colors hover:text-primary ${
                  location.pathname === link.href ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Coaches link always visible */}
            <Link
              to="/coaches"
              className={`font-medium transition-colors hover:text-primary ${
                location.pathname === '/coaches' ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              Coaches
            </Link>

            {/* Youth Athletes link always visible */}
            <Link
              to="/youth-athletes"
              className={`font-medium transition-colors hover:text-primary ${
                location.pathname === '/youth-athletes' ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              Youth
            </Link>

            {/* Sponsorship link always visible */}
            <Link
              to="/sponsors"
              className={`font-medium transition-colors hover:text-primary ${
                location.pathname.startsWith('/sponsor') ? 'text-primary' : 'text-muted-foreground'
              }`}
              data-testid="nav-sponsors-link"
            >
              Sponsors
            </Link>

            {/* Events link always visible */}
            <Link
              to="/events"
              className={`font-medium transition-colors hover:text-primary ${
                location.pathname === '/events' ? 'text-primary' : 'text-muted-foreground'
              }`}
            >
              Events
            </Link>

            {/* Gallery link always visible */}
            <Link
              to="/gallery"
              className={`font-medium transition-colors hover:text-primary ${
                location.pathname.startsWith('/gallery') ? 'text-primary' : 'text-muted-foreground'
              }`}
              data-testid="nav-gallery-link"
            >
              Gallery
            </Link>

            {isAuthenticated ? (
              <div className="flex items-center gap-4">
                {/* Critique Credits Display - hide for admins; only relevant for critique requests now */}
                {!isAdmin && (
                  <div className="hidden lg:block text-sm font-medium bg-muted px-3 py-1.5 rounded-full" data-testid="critique-credits-chip">
                    <span className="text-muted-foreground">Critique Credits:</span>{' '}
                    <span className="text-foreground font-bold">{user?.credits || 0}</span>
                  </div>
                )}

                {/* Notification Bell */}
                <NotificationBell />

                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <span className="font-medium">{user?.name}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    {/* For coaches (not admin): Show only Coach Dashboard */}
                    {isCoach && !isAdmin && (
                      <DropdownMenuItem onClick={() => navigate('/coach')} data-testid="menu-coach">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Coach Dashboard
                      </DropdownMenuItem>
                    )}
                    {/* For regular users (not coach, not admin): Show Dashboard and Profile */}
                    {!isCoach && !isAdmin && (
                      <>
                        <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                          <LayoutDashboard className="w-4 h-4 mr-2" />
                          Dashboard
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                          <Settings className="w-4 h-4 mr-2" />
                          Profile & Settings
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate('/agreements')} data-testid="menu-agreements">
                          <FileText className="w-4 h-4 mr-2" />
                          My Agreements
                        </DropdownMenuItem>
                      </>
                    )}
                    {/* For admins and stream managers: Show Admin Panel */}
                    {(isAdmin || isStreamManager) && (
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                        <Shield className="w-4 h-4 mr-2" />
                        Admin Panel
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="w-4 h-4 mr-2" />
                      Log Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login">
                  <Button variant="ghost" data-testid="login-btn">Log In</Button>
                </Link>
                <Link to="/signup">
                  <Button className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full px-6" data-testid="signup-btn">
                    Get Started
                  </Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 touch-target"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background border-b border-border animate-fade-in max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-4 space-y-3 pb-32">
            {isAuthenticated ? (
              <>
                <div className="flex items-center justify-between py-2 border-b border-border">
                  <span className="font-medium">{user?.name}</span>
                  {!isAdmin && (
                    <span className="text-sm bg-muted px-3 py-1 rounded-full">
                      {user?.credits || 0} Critique Credits
                    </span>
                  )}
                  {isAdmin && (
                    <span className="text-sm bg-primary/10 text-primary px-3 py-1 rounded-full">
                      Admin
                    </span>
                  )}
                </div>
                
                {/* Dashboard/Admin links */}
                {(isAdmin || isStreamManager) ? (
                  <Link
                    to="/admin"
                    className="block py-3 font-medium hover:text-primary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Admin Panel
                  </Link>
                ) : isCoach ? (
                  <Link
                    to="/coach"
                    className="block py-3 font-medium hover:text-primary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Coach Dashboard
                  </Link>
                ) : (
                  <Link
                    to="/dashboard"
                    className="block py-3 font-medium hover:text-primary"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                )}
                
                {/* Main navigation links - same as desktop */}
                <Link
                  to="/library"
                  className="block py-3 font-medium hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Library
                </Link>
                <Link
                  to="/community"
                  className="block py-3 font-medium hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Community
                </Link>
                <Link
                  to="/marketplace"
                  className="block py-3 font-medium hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Marketplace
                </Link>
                <Link
                  to="/events"
                  className="block py-3 font-medium hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Events
                </Link>
                <Link
                  to="/gallery"
                  className="block py-3 font-medium hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Gallery
                </Link>
                <Link
                  to="/coaches"
                  className="block py-3 font-medium hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Coaches
                </Link>
                <Link
                  to="/youth-athletes"
                  className="block py-3 font-medium hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Youth
                </Link>
                <Link
                  to="/sponsors"
                  className="block py-3 font-medium hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sponsors
                </Link>
                
                {/* Profile link for regular users */}
                {!isAdmin && !isCoach && (
                  <>
                    <Link
                      to="/profile"
                      className="block py-3 font-medium hover:text-primary"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Profile & Settings
                    </Link>
                    <Link
                      to="/agreements"
                      className="block py-3 font-medium hover:text-primary"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      My Agreements
                    </Link>
                  </>
                )}
                
                <div className="border-t border-border pt-3 mt-3">
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="block w-full text-left py-3 font-medium text-destructive"
                  >
                    Log Out
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Public pages for unauthenticated users */}
                <Link
                  to="/about"
                  className="block py-3 font-medium hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-coaches-link"
                >
                  Coaches
                </Link>
                <Link
                  to="/youth-athletes"
                  className="block py-3 font-medium hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-youth-athletes-link"
                >
                  Youth
                </Link>
                <Link
                  to="/sponsorship"
                  className="block py-3 font-medium hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-sponsorship-link"
                >
                  Sponsorship
                </Link>
                <Link
                  to="/events"
                  className="block py-3 font-medium hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-events-link"
                >
                  Events
                </Link>
                <Link
                  to="/marketplace"
                  className="block py-3 font-medium hover:text-primary"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-marketplace-link"
                >
                  Marketplace
                </Link>
                <div className="border-t border-border pt-3 mt-3">
                  <Link
                    to="/login"
                    className="block py-3 font-medium hover:text-primary"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-login-link"
                  >
                    Log In
                  </Link>
                  <Link
                    to="/signup"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid="mobile-signup-link"
                  >
                    <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground rounded-full mt-2">
                      Get Started
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
